/**
 * Product API functions
 */

import { ProductListResponse, ProductResponse } from '../types/product';
import { STRAPI_URL } from './base';

/**
 * Fetch all products with pagination and filtering
 */
export async function fetchProducts(params?: {
  page?: number;
  pageSize?: number;
  isActive?: boolean;
  categorySlug?: string;
}): Promise<ProductListResponse> {
  try {
    const searchParams = new URLSearchParams();

    // Add pagination parameters
    if (params?.page) {
      searchParams.append('pagination[page]', params.page.toString());
    }
    if (params?.pageSize) {
      searchParams.append('pagination[pageSize]', params.pageSize.toString());
    }

    // Add filtering parameters
    if (params?.isActive !== undefined) {
      searchParams.append('filters[isActive][$eq]', params.isActive.toString());
    }

    // Category filter
    if (params?.categorySlug) {
      searchParams.append('filters[category][slug][$eq]', params.categorySlug);
    }

    // Add population for images and category
    searchParams.append('populate[images]', 'true');
    searchParams.append('populate[category]', 'true');

    // Add sorting by order and createdAt
    searchParams.append('sort[0]', 'order:asc');
    searchParams.append('sort[1]', 'createdAt:desc');

    const url = `${STRAPI_URL}/api/products?${searchParams.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.NEXT_PUBLIC_STRAPI_API_TOKEN && {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
        }),
      },
      next: { revalidate: 60 }, // Cache for 1 minute
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Product API error:', response.status, errorText);
      throw new Error(
        `Failed to fetch products: ${response.status} - ${errorText}`
      );
    }

    const result: ProductListResponse = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
}

/**
 * Fetch a single product by slug
 */
export async function fetchProductBySlug(
  slug: string
): Promise<ProductResponse> {
  try {
    const searchParams = new URLSearchParams();
    searchParams.append('filters[slug][$eq]', slug);
    searchParams.append('populate[images]', 'true');

    const url = `${STRAPI_URL}/api/products?${searchParams.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.NEXT_PUBLIC_STRAPI_API_TOKEN && {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
        }),
      },
      next: { revalidate: 60 }, // Cache for 1 minute
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Product API error:', response.status, errorText);
      throw new Error(
        `Failed to fetch product: ${response.status} - ${errorText}`
      );
    }

    const result: ProductListResponse = await response.json();

    if (!result.data || result.data.length === 0) {
      throw new Error(`Product with slug "${slug}" not found`);
    }

    return {
      data: result.data[0],
      meta: result.meta,
    };
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
}

/**
 * Fetch related products (excluding current product)
 */
export async function fetchRelatedProducts(
  currentProductId: number,
  limit: number = 3
): Promise<ProductListResponse> {
  try {
    const searchParams = new URLSearchParams();
    searchParams.append('filters[id][$ne]', currentProductId.toString());
    searchParams.append('filters[isActive][$eq]', 'true');
    searchParams.append('pagination[pageSize]', limit.toString());
    searchParams.append('populate[images]', 'true');
    searchParams.append('sort[0]', 'order:asc');
    searchParams.append('sort[1]', 'createdAt:desc');

    const url = `${STRAPI_URL}/api/products?${searchParams.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...(process.env.NEXT_PUBLIC_STRAPI_API_TOKEN && {
          Authorization: `Bearer ${process.env.NEXT_PUBLIC_STRAPI_API_TOKEN}`,
        }),
      },
      next: { revalidate: 60 }, // Cache for 1 minute
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Related products API error:', response.status, errorText);
      throw new Error(
        `Failed to fetch related products: ${response.status} - ${errorText}`
      );
    }

    const result: ProductListResponse = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching related products:', error);
    throw error;
  }
}
